import { isFreeOrg, isFreeAdmin, getFreeRouteDecision } from '@/utils/organizationEntitlements';

/**
 * shortcutPermissions — UX gate for dashboard shortcut screens.
 *
 * Mirrors the permission/tab checks already used across the app so presets are
 * never offered and saved shortcuts stay hidden when the user loses access.
 * Display-only: server-side RLS/RPC remains the real enforcement, so unknown
 * routes fail OPEN (custom links to ungated screens keep working).
 */

// Ordered longest-prefix rules. `check(user)` returns true when access is allowed.
// Where a screen has no permission gate in-app (verified against the route),
// the prefix is listed explicitly as open for clarity.
const SCREEN_RULES = [
    // People — '/people/create?id=…' is the edit flow (self-edit allowed), handled below.
    { prefix: '/people/create', check: (user) => user?.can?.('Create People') },
    { prefix: '/people', check: (user, { isOwnProfile }) => isOwnProfile || !!user?.can?.('View People') },
    // Events — index/blackouts are ungated in-app (events/index.jsx offers
    // "Add Blackout" to everyone); only series creation is permission-gated.
    { prefix: '/events/create', check: (user) => user?.can?.('Create Events') },
    { prefix: '/events', check: () => true },
    // Tasks — list is open (tab option has no gate); creation is gated.
    { prefix: '/tasks/teams/create', check: (user) => user?.can?.('Create Tasks') },
    { prefix: '/tasks/create', check: (user) => user?.can?.('Create Tasks') },
    { prefix: '/tasks', check: () => true },
    // Small Groups — index and session logging are offered to members in-app
    // (row action ungated); type administration and group creation are gated.
    { prefix: '/smallgroups/types', check: (user) => !!user?.canAny?.(['Create Small Group Types', 'Edit Small Group Types', 'Delete Small Group Types']) },
    { prefix: '/smallgroups/create', check: (user) => user?.can?.('Create Small Groups') },
    { prefix: '/smallgroups', check: () => true },
    // Bible Studies — org flag + rostered teacher or module permission
    // (mirrors the useBibleStudies gate used by the screens themselves).
    // '/biblestudies/reports' — rostered teachers (own scope) or reports/manage
    // permission (all scope); 'View Bible Studies' alone does not grant reports.
    // Teacher details live inside reports as ?tab=teachers (+ teacherIDs),
    // so this one rule covers both.
    { prefix: '/biblestudies/reports', check: (user, { organization, smallGroupTypes }) => {
        if (organization?.enableBibleStudies !== true) return false;
        const roleID = smallGroupTypes?.find(t => t.purpose === 'BIBLE_STUDY' && t.roleID)?.roleID;
        return !!(user?.isAdmin || user?.can?.('Manage Bible Studies') || user?.can?.('View Bible Study Reports') || (roleID && user?.Roles?.some(r => r.id === roleID)));
    }},
    { prefix: '/biblestudies/create', check: (user, { organization, smallGroupTypes }) => {
        if (organization?.enableBibleStudies !== true) return false;
        const roleID = smallGroupTypes?.find(t => t.purpose === 'BIBLE_STUDY' && t.roleID)?.roleID;
        return !!(user?.isAdmin || user?.can?.('Manage Bible Studies') || user?.can?.('Create Bible Studies') || (roleID && user?.Roles?.some(r => r.id === roleID)));
    }},
    { prefix: '/biblestudies', check: (user, { organization, smallGroupTypes }) => {
        if (organization?.enableBibleStudies !== true) return false;
        const roleID = smallGroupTypes?.find(t => t.purpose === 'BIBLE_STUDY' && t.roleID)?.roleID;
        return !!(user?.isAdmin || user?.can?.('Manage Bible Studies') || user?.can?.('View Bible Studies') || (roleID && user?.Roles?.some(r => r.id === roleID)));
    }},
    // Giving management — screen renders a denial message without the permission.
    { prefix: '/contributions', check: (user) => !!user?.can?.('View Contributions') },
    // Connect — boards are member-facing; conversations/announcements gated per connect/index.jsx.
    { prefix: '/connect/conversations', check: (user) => !!user?.can?.('View Conversations') },
    { prefix: '/connect/announcements', check: (user) => !!user?.can?.('Send SMS Messages') },
    { prefix: '/connect', check: () => true },
    // Roles & Permissions — mirrors the roles tab option check.
    { prefix: '/roles', check: (user) => !!user?.canAny?.(['Create Roles', 'Edit Roles', 'Delete Roles']) || (user?.ManagingRoles?.length || 0) > 0 },
    { prefix: '/permissions', check: (user) => !!user?.canAny?.(['Create Roles', 'Edit Roles', 'Delete Roles']) || (user?.ManagingRoles?.length || 0) > 0 },
    // Admin surfaces.
    { prefix: '/registrations', check: (user) => !!user?.isAdmin },
    { prefix: '/customfields', check: (user) => !!user?.can?.('Edit Custom Fields') },
    { prefix: '/settings/organization', check: (user) => !!user?.isAdmin },
    // Module tabs gated in tabOptions.js.
    { prefix: '/training', check: (user) => !!user?.can?.('View Training') },
    { prefix: '/automations', check: (user) => !!user?.can?.('View Paths') },
    { prefix: '/paths', check: (user) => !!user?.can?.('View Paths') }, // legacy alias
    // Follow Up — same 'paths' module entitlement, its own permission.
    { prefix: '/follow-up', check: (user) => !!user?.can?.('View Follow Up') },
    { prefix: '/files', check: (user) => !!user?.can?.('View Files') },
    { prefix: '/assistant', check: (user) => !!user?.can?.('Use AI Assistant') },
];

const SORTED_RULES = [...SCREEN_RULES].sort((a, b) => b.prefix.length - a.prefix.length);

// Small-group-type scoping — mirrors the role gate in smallgroups/index.jsx
// and getTabOptionsForUser: type.roleID must match a user role (admins pass).
const canAccessTypeID = (typeID, user, smallGroupTypes) => {
    if (!typeID) return true;
    const type = smallGroupTypes?.find((t) => String(t.id) === String(typeID));
    if (!type?.roleID) return true; // unknown type → fail open
    return !!user?.isAdmin || !!user?.Roles?.some((r) => r.id === type.roleID);
};

const parseQuery = (queryString) => {
    const params = {};
    new URLSearchParams(queryString || '').forEach((value, key) => { params[key] = value; });
    return params;
};

/**
 * Can the user access this shortcut screen?
 * @param {string} screen - stored shortcut route, e.g. '/tasks/create' or '/smallgroups?typeID=3'
 * @param {object} user - loggedInUser (can/canAny/isAdmin/Roles/ManagingRoles bound)
 * @param {object} entitlements - get_organization_entitlements payload (may be null)
 * @param {Array} smallGroupTypes - for typeID-scoped role gating (optional)
 * @param {object} organization - org row for feature-flag rules (optional)
 * @returns {boolean}
 */
export const canAccessScreen = (screen, user, entitlements = null, smallGroupTypes = [], organization = null) => {
    if (!screen || typeof screen !== 'string') return false;

    const [pathname, queryString] = screen.split('?');
    const path = pathname.replace(/\/+$/, '') || '/';
    const segments = path.split('/');
    const params = parseQuery(queryString);

    // Free plan: defer to the shared route-decision rules, then mirror the
    // Free profile's reduced tab list — people/[personID].jsx keeps only
    // family/notes/giving, and notes still needs its permission/admin gate.
    if (isFreeOrg(entitlements)) {
        if (getFreeRouteDecision(path, entitlements) !== 'allow') return false;
        if (segments[1] === 'people' && params.tab) {
            if (params.tab === 'notes') {
                return !!(user?.can?.('View People Notes') || isFreeAdmin(entitlements));
            }
            return ['family', 'giving'].includes(params.tab);
        }
        return true;
    }

    // Self-edit flow: '/people/create?id=<self>' (or any edit) is not a create.
    if (path === '/people/create' && params.id) return true;

    const isOwnProfile = segments[1] === 'people' && !!user?.id && String(segments[2]) === String(user.id);

    // Profile tab deep links mirror the tab gates in people/[personID].jsx:
    // notes needs View People Notes/admin even on your own profile; giving on
    // someone else's profile needs View Contributions; followup needs the
    // Follow Up team permission.
    if (segments[1] === 'people' && params.tab) {
        if (params.tab === 'notes' && !(user?.can?.('View People Notes') || user?.isAdmin)) {
            return false;
        }
        if (params.tab === 'giving' && !isOwnProfile && !user?.can?.('View Contributions')) {
            return false;
        }
        if (params.tab === 'followup' && !user?.can?.('View Follow Up')) {
            return false;
        }
    }

    // Small-group-type scoping (saved '/smallgroups?typeID=…' and presets).
    if (segments[1] === 'smallgroups' && !canAccessTypeID(params.typeID, user, smallGroupTypes)) {
        return false;
    }

    const rule = SORTED_RULES.find((r) => path === r.prefix || path.startsWith(r.prefix + '/'));
    if (!rule) return true; // unknown route → fail open, backend still enforces
    return rule.check(user, { isOwnProfile, params, organization, smallGroupTypes }) !== false;
};
